<template>
  <chart-by-month
    :resultSetByMonth="resultSetByMonth"
    :chartDataMonth="chartDataMonthNCDJ"
    :chartDataAverage="chartDataAverageNCDJ"
    :graph="graph"
    :displayed="displayed"
  />
</template>

<script>
import ChartByMonth from './ChartByMonth';
import { resultList, monthLabels, showColor } from './ReadingsUtils';

export default {
  name: 'ReadingsHeatingByMonth',
  components: {
    ChartByMonth,
  },
  props: {
    resultSetByMonth: {
      type: Array,
    },
    graph: {
      type: Number,
      default: 1,
    },
    displayed: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {};
  },
  methods: {
    showColor,
    getAvg(array) {
      return array
        .map((el) => ({
          conso: el.nc_dj * el.dj_reel,
          dj: el.dj_reel,
        }))
        .reduce(
          (a, b) => ({
            conso: a.conso + b.conso,
            dj: a.dj + b.dj,
          }),
          {
            conso: 0,
            dj: 0,
          }
        );
    },
  },
  computed: {
    years() {
      return this.resultSetByMonth[0] && this.resultSetByMonth[0].an
        ? [
            this.resultSetByMonth[0].an,
            this.resultSetByMonth[0].an + 1,
            this.resultSetByMonth[0].an + 2,
          ]
        : [];
    },
    yearsData() {
      return this.years.map((year) =>
        this.resultSetByMonth.filter((item) => item.an === year)
      );
    },
    chartDataMonthNCDJ() {
      // Transform data for visualization
      if (this.years.length) {
        const items = this.resultSetByMonth;
        const labels = monthLabels.map((el) => el.id);
        const datasets = [];
        const labDJ = resultList.find((el) => el.value === 'dj_reel').text;
        datasets.push({
          label: `${labDJ} ${this.years[0]}`,
          data: labels.map((element) => {
            const val = this.yearsData[0].find((elem) => elem.mois === element);
            if (val) {
              return val.dj_reel;
            }
            return null;
          }),
          _short: true,
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 2,
          yAxisID: 'yRight1',
        });
        datasets.push({
          label: `${labDJ} ${this.years[1]}`,
          data: labels.map((element) => {
            const val = this.yearsData[1].find((elem) => elem.mois === element);
            if (val) {
              return val.dj_reel;
            }
            return null;
          }),
          _short: true,
          borderColor: 'rgba(145, 56, 0, 1)',
          borderWidth: 2,
          yAxisID: 'yRight1',
        });
        datasets.push({
          label: `${labDJ} ${this.years[2]}`,
          data: labels.map((element) => {
            const val = this.yearsData[2].find((elem) => elem.mois === element);
            if (val) {
              return val.dj_reel;
            }
            return null;
          }),
          _short: true,
          borderColor: 'rgba(200, 200, 50, 1)',
          borderWidth: 2,
          yAxisID: 'yRight1',
        });

        const labNC = resultList.find((el) => el.value === 'nc_dj').text;
        datasets.push({
          type: 'line',
          label: `${labNC} ${this.years[0]}`,
          data: labels.map((element) => {
            const val = this.yearsData[0].find((elem) => elem.mois === element);
            if (val) {
              return val.nc_dj;
            }
            return null;
          }),
          _short: true,
          borderColor: 'rgba(75, 192, 192, 1)',
          backgroundColor: 'rgba(0, 0, 0, 0)',
          yAxisID: 'yLeft1',
        });
        datasets.push({
          type: 'line',
          label: `${labNC} ${this.years[1]}`,
          data: labels.map((element) => {
            const val = this.yearsData[1].find((elem) => elem.mois === element);
            if (val) {
              return val.nc_dj;
            }
            return null;
          }),
          _short: true,
          borderColor: 'rgba(145, 56, 0, 1)',
          backgroundColor: 'rgba(0, 0, 0, 0)',
          yAxisID: 'yLeft1',
        });
        datasets.push({
          type: 'line',
          label: `${labNC} ${this.years[2]}`,
          data: labels.map((element) => {
            const val = this.yearsData[2].find((elem) => elem.mois === element);
            if (val) {
              return val.nc_dj;
            }
            return null;
          }),
          _short: true,
          borderColor: 'rgba(200, 200, 50, 1)',
          backgroundColor: 'rgba(0, 0, 0, 0)',
          yAxisID: 'yLeft1',
        });

        const labNCo = resultList.find((el) => el.value === 'nc_dj_obj').text;
        datasets.push({
          type: 'line',
          label: `${labNCo} ${this.years[2]}`,
          data: labels.map((element) => {
            const val = this.yearsData[2].find((elem) => elem.mois === element);
            if (val) {
              return val.nc_dj_obj;
            }
            return null;
          }),
          _short: true,
          borderColor: this.showColor('main'),
          backgroundColor: 'rgba(0, 0, 0, 0)',
          yAxisID: 'yLeft1',
        });

        return {
          labels,
          items,
          datasets,
        };
      }
      return {
        labels: [],
        items: [],
        datasets: [],
      };
    },

    chartDataAverageNCDJ() {
      // Transform data for visualization
      if (this.years.length) {
        const items = this.resultSetByMonth;
        const labels = ['A-2', 'A-1', 'En cours', 'Moyenne'];
        const datasets = [];
        const labNCDJ = resultList.find((el) => el.value === 'nc_dj').text;
        const averageCurrent = this.getAvg(this.yearsData[2]);
        const averageMinus1 = this.getAvg(this.yearsData[1]);
        const averageMinus2 = this.getAvg(this.yearsData[0]);
        if (averageMinus2.conso) {
          datasets.push({
            label: `${labNCDJ} A-2`,
            data: [
              averageMinus2.dj
                ? (averageMinus2.conso / averageMinus2.dj).toFixed(3)
                : null,
              null,
              null,
            ],
            _short: true,
            borderColor: 'rgba(75, 192, 192, 1)',
            borderWidth: 2,
            yAxisID: 'yLeft1',
          });
        }

        if (averageMinus1.conso) {
          datasets.push({
            label: `${labNCDJ} A-1`,
            data: [
              null,
              averageMinus1.dj
                ? (averageMinus1.conso / averageMinus1.dj).toFixed(3)
                : null,
            ],
            _short: true,
            borderColor: 'rgba(145, 56, 0, 1)',
            borderWidth: 2,
            yAxisID: 'yLeft1',
          });
        }

        datasets.push({
          label: `${labNCDJ} En cours`,
          data: [
            null,
            null,
            averageCurrent.dj
              ? (averageCurrent.conso / averageCurrent.dj).toFixed(3)
              : null,
          ],
          _short: true,
          borderColor: 'rgba(200, 200, 50, 1)',
          borderWidth: 2,
          yAxisID: 'yLeft1',
        });

        datasets.push({
          label: `${labNCDJ} Moyen`,
          data: [
            null,
            null,
            null,
            averageCurrent.dj
              ? (
                  (averageCurrent.conso +
                    averageMinus1.conso +
                    averageMinus2.conso) /
                  (averageCurrent.dj + averageMinus1.dj + averageMinus2.dj)
                ).toFixed(3)
              : null,
          ],
          _short: true,
          borderColor: 'rgba(192, 192, 192, 1)',
          borderWidth: 2,
          yAxisID: 'yLeft1',
        });

        return {
          labels,
          items,
          datasets,
        };
      }
      return {
        labels: [],
        items: [],
        datasets: [],
      };
    },
  },
};
</script>
